import { useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Icon } from './Icon';
import { DentroFoglio, MobileSheet } from './MobileSheet';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useUrlState } from '../../hooks/useUrlState';
import './ordina.css';

/**
 * "Ordina per" e "Filtra per": lo stesso controllo in tutte le liste.
 *
 * Tre pezzi, da usare insieme o da soli:
 *   useOrdina(opzioni)       l'ordinamento scelto, in `?ordina=` (la prima
 *                            opzione è la predefinita e non va nell'URL)
 *   useFiltri(defs, items?)  i filtri scelti, ognuno col suo parametro dal nome
 *                            parlante (`?categoria=SAAS,UTENZE&solo=scadute`)
 *   <OrdinaFiltra />         il controllo: sul computer due bottoni con il loro
 *                            menu a tendina (.dropdown, lo stile dei menu di
 *                            tutta l'app); sul telefono, dentro il pannello dei
 *                            filtri (MobileFilters), si mostra disteso; fuori da
 *                            un pannello diventa un bottone "Ordina e filtra"
 *                            che ne apre uno dal basso.
 *
 * Più gli helper per ordinare nel browser: perNumero, perData, perTesto
 * (italiano, con le cifre contate come numeri: "Fattura 9" prima di
 * "Fattura 10"), e ordinaInMemoria. I valori vuoti vanno SEMPRE in fondo,
 * in tutti e due i versi: "senza data" non sale mai in cima.
 *
 * REGOLA DELLE ETICHETTE: ogni voce dice di cosa parla e si capisce letta da
 * sola. "Prima le più costose", "Prossimo addebito prima", "Nome dalla A alla
 * Z", "Solo le spese sospese". Mai aggettivi soli ("Recenti") né sigle.
 *
 * Cambiare ordine o filtri non tocca la ricerca (`q`) né la vista (`vista`):
 * ogni parametro è suo. "Togli i filtri" toglie solo i filtri.
 *
 * ── Esempio: lista che arriva intera (si ordina e filtra nel browser) ──
 *
 *   const ORDINI: OpzioneOrdina<Spesa>[] = [
 *     { key: 'prossimo', label: 'Prossimo addebito prima', criteri: [perData((s) => s.nextDueDate)] },
 *     { key: 'costose',  label: 'Prima le più costose',    criteri: [perNumero((s) => s.alMese, 'decrescente')] },
 *     { key: 'nome',     label: 'Nome dalla A alla Z',     criteri: [perTesto((s) => s.name)] },
 *   ];
 *
 *   function Lista({ items }: { items: Spesa[] }) {
 *     const ordina = useOrdina(ORDINI);                       // ?ordina=costose
 *     const filtri = useFiltri<Spesa>([
 *       { param: 'categoria', label: 'Categoria della spesa', multi: true,
 *         opzioni: [{ value: 'SAAS', label: 'Software e abbonamenti' }, …],
 *         test: (s, v) => s.category === v },
 *       { param: 'solo', label: 'Quali spese',
 *         opzioni: [{ value: 'scadute', label: 'Solo le spese scadute' }],
 *         test: (s) => s.nextDueDate! < oggi },
 *     ], items);                                              // items: per i conteggi
 *     const righe = useMemo(() => ordina.applica(filtri.applica(items)), [items, ordina, filtri]);
 *     return (
 *       <MobileFilters attivi={filtri.attivi + (q ? 1 : 0)} label="Cerca, ordina e filtra">
 *         <div className="filterbar filterbar--float">
 *           …ricerca…
 *           <OrdinaFiltra ordina={ordina} filtri={filtri} />
 *         </div>
 *       </MobileFilters>
 *     );
 *   }
 *
 * ── Esempio: lista paginata dal server ──
 *   Le opzioni non hanno `criteri` e i filtri non hanno `test`: la chiave va
 *   all'API, che ordina lei (parametro `sort`, validato con zod).
 *
 *   const ordina = useOrdina([{ key: 'recenti', label: 'Pagate più di recente' }, …]);
 *   const filtri = useFiltri([{ param: 'tipo', label: 'Tipo di spesa', opzioni: […] }]);
 *   useEffect(() => setPage(1), [ordina.key, filtri.chiave]);   // si riparte da pagina 1
 *   useQuery(lista({ sort: ordina.key, tipo: filtri.valore('tipo'), page }));
 *
 * Il CSS sta in ordina.css (prefisso `of-`): index.css e mobile.css non si
 * toccano, il contenitore del menu è il `.dropdown` di tutti.
 */

// ════════════════════════════════════════════════════════════════════
// Confronti per ordinare nel browser
// ════════════════════════════════════════════════════════════════════

export type Confronto<T> = (a: T, b: T) => number;
export type Verso = 'crescente' | 'decrescente';

const isVuoto = (v: unknown) => v == null || v === '' || (typeof v === 'number' && Number.isNaN(v));

/** I vuoti in fondo in tutti e due i versi; il verso vale solo fra valori veri. */
function conVuotiInFondo<T, V>(get: (x: T) => V | null | undefined, cmp: (a: V, b: V) => number, verso: Verso): Confronto<T> {
  const s = verso === 'decrescente' ? -1 : 1;
  return (a, b) => {
    const va = get(a);
    const vb = get(b);
    const ea = isVuoto(va);
    const eb = isVuoto(vb);
    if (ea || eb) return ea === eb ? 0 : ea ? 1 : -1;
    return s * cmp(va as V, vb as V);
  };
}

/** Importi, conteggi, giorni. */
export function perNumero<T>(get: (x: T) => number | null | undefined, verso: Verso = 'crescente'): Confronto<T> {
  return conVuotiInFondo(get, (a, b) => a - b, verso);
}

function istante(v: string | Date): number {
  if (v instanceof Date) return v.getTime();
  // "YYYY-MM-DD" da solo è mezzanotte UTC; con l'ora è un istante vero.
  const t = Date.parse(v.length === 10 ? `${v}T00:00:00Z` : v);
  return Number.isNaN(t) ? 0 : t;
}

/** Date "YYYY-MM-DD", ISO complete o Date. Crescente = la più vicina nel tempo prima. */
export function perData<T>(get: (x: T) => string | Date | null | undefined, verso: Verso = 'crescente'): Confronto<T> {
  return conVuotiInFondo(get, (a, b) => istante(a) - istante(b), verso);
}

// Lo stesso di localeCompare('it'), ma costruito una volta sola.
const COLLATOR = new Intl.Collator('it', { sensitivity: 'base', numeric: true });

/** Testo in italiano: maiuscole e accenti non contano, le cifre sì come numeri. */
export function perTesto<T>(get: (x: T) => string | null | undefined, verso: Verso = 'crescente'): Confronto<T> {
  return conVuotiInFondo(get, (a: string, b: string) => COLLATOR.compare(a, b), verso);
}

/** Una copia ordinata: il primo criterio che distingue vince, a parità resta l'ordine di arrivo. */
export function ordinaInMemoria<T>(items: readonly T[], criteri: ReadonlyArray<Confronto<T>>): T[] {
  const copia = [...items];
  if (!criteri.length) return copia;
  return copia.sort((a, b) => {
    for (const c of criteri) {
      const r = c(a, b);
      if (r) return r;
    }
    return 0;
  });
}

// ════════════════════════════════════════════════════════════════════
// Ordina per
// ════════════════════════════════════════════════════════════════════

export interface OpzioneOrdina<T = unknown> {
  /** Il valore in `?ordina=` (e, nelle liste del server, il `sort` dell'API). */
  key: string;
  /** L'etichetta intera: "Prima le più costose", "Nome dalla A alla Z". */
  label: string;
  /** Solo per le liste ordinate nel browser. Senza: l'ordine com'è arrivato. */
  criteri?: ReadonlyArray<Confronto<T>>;
}

export interface StatoOrdina<T = unknown> {
  opzioni: ReadonlyArray<OpzioneOrdina<T>>;
  /** La chiave attiva, sempre una delle opzioni (un valore strano nell'URL torna alla predefinita). */
  key: string;
  attiva: OpzioneOrdina<T>;
  /** È quella predefinita? */
  predefinita: boolean;
  set: (key: string) => void;
  /** Ordina una lista con i `criteri` dell'opzione attiva. */
  applica: (items: readonly T[]) => T[];
}

/**
 * L'ordinamento scelto, in `?ordina=`. La predefinita è la prima opzione (o
 * `predefinita`): con quella il parametro sparisce dall'URL.
 * Le opzioni vanno dichiarate FUORI dal componente (costante di modulo).
 */
export function useOrdina<T = unknown>(
  opzioni: ReadonlyArray<OpzioneOrdina<T>>,
  opts: { param?: string; predefinita?: string } = {},
): StatoOrdina<T> {
  const param = opts.param ?? 'ordina';
  const def = opts.predefinita ?? opzioni[0]?.key ?? '';
  const [raw, setRaw] = useUrlState<string>(param, def, { replace: true });
  const attiva = opzioni.find((o) => o.key === raw) ?? opzioni.find((o) => o.key === def) ?? opzioni[0]!;
  const applica = useCallback(
    (items: readonly T[]) => (attiva.criteri ? ordinaInMemoria(items, attiva.criteri) : [...items]),
    [attiva],
  );
  return useMemo(
    () => ({ opzioni, key: attiva.key, attiva, predefinita: attiva.key === def, set: setRaw, applica }),
    [opzioni, attiva, def, setRaw, applica],
  );
}

// ════════════════════════════════════════════════════════════════════
// Filtra per
// ════════════════════════════════════════════════════════════════════

export interface OpzioneFiltro {
  /** Il valore nell'URL. */
  value: string;
  /** L'etichetta intera: "Solo le spese scadute", "Software e abbonamenti online". */
  label: string;
  /** Quante righe ha: se manca e il filtro ha `test`, la conta useFiltri. */
  count?: number;
}

export interface FiltroDef<T = unknown> {
  /** Il parametro nell'URL, con un nome parlante: `categoria`, `metodo`, `solo`. */
  param: string;
  /** Il titolo del gruppo nel menu: "Categoria della spesa", "Come si paga". */
  label: string;
  /** Più scelte insieme (nell'URL separate da virgola). Senza: una sola, e ritoccarla la toglie. */
  multi?: boolean;
  opzioni: ReadonlyArray<OpzioneFiltro>;
  /** Solo per le liste filtrate nel browser: la riga passa se vale per ALMENO una delle scelte. */
  test?: (item: T, value: string) => boolean;
}

export interface StatoFiltri<T = unknown> {
  /** Le definizioni, con i conteggi riempiti. */
  defs: ReadonlyArray<FiltroDef<T>>;
  /** Le scelte attive per parametro (vuoto = nessun filtro su quel gruppo). */
  valori: Record<string, string[]>;
  /** La prima scelta di un gruppo, o '' (comodo per i filtri a scelta singola e per le API). */
  valore: (param: string) => string;
  /** Quante scelte sono accese in tutto: il numero sul bottone. */
  attivi: number;
  /** Una stringa che cambia quando cambiano i filtri: per le dipendenze (tornare a pagina 1). */
  chiave: string;
  ha: (param: string, value: string) => boolean;
  toggle: (param: string, value: string) => void;
  set: (param: string, values: string[]) => void;
  /** Toglie tutti i filtri (e solo quelli: ricerca, vista e ordine restano). */
  clear: () => void;
  /** Filtra una lista con i `test`: dentro un gruppo basta una scelta, fra gruppi servono tutte. */
  applica: (items: readonly T[]) => T[];
}

const splitVal = (v: string | null) => (v ? v.split(',').map((x) => x.trim()).filter(Boolean) : []);

/**
 * I filtri scelti, ognuno nel suo parametro dell'URL. `items` è facoltativo:
 * se c'è, i conteggi delle opzioni con `test` si calcolano da soli.
 */
export function useFiltri<T = unknown>(defs: ReadonlyArray<FiltroDef<T>>, items?: readonly T[]): StatoFiltri<T> {
  const [params, setParams] = useSearchParams();

  const valori = useMemo(() => {
    const out: Record<string, string[]> = {};
    for (const d of defs) {
      const v = splitVal(params.get(d.param));
      out[d.param] = d.multi ? v : v.slice(0, 1);
    }
    return out;
  }, [defs, params]);

  const chiave = defs.map((d) => `${d.param}=${valori[d.param]!.join(',')}`).join('&');

  const set = useCallback((param: string, values: string[]) => {
    setParams((prev) => {
      const sp = new URLSearchParams(prev);
      if (values.length) sp.set(param, values.join(','));
      else sp.delete(param);
      return sp;
    }, { replace: true });
  }, [setParams]);

  const toggle = useCallback((param: string, value: string) => {
    const d = defs.find((x) => x.param === param);
    const cur = valori[param] ?? [];
    const on = cur.includes(value);
    if (d?.multi) set(param, on ? cur.filter((x) => x !== value) : [...cur, value]);
    else set(param, on ? [] : [value]);
  }, [defs, valori, set]);

  const clear = useCallback(() => {
    setParams((prev) => {
      const sp = new URLSearchParams(prev);
      for (const d of defs) sp.delete(d.param);
      return sp;
    }, { replace: true });
  }, [defs, setParams]);

  const applica = useCallback((list: readonly T[]) => {
    const accesi = defs.filter((d) => d.test && (valori[d.param]?.length ?? 0) > 0);
    if (!accesi.length) return [...list];
    return list.filter((it) => accesi.every((d) => valori[d.param]!.some((v) => d.test!(it, v))));
  }, [defs, valori]);

  const defsContate = useMemo(() => {
    if (!items) return defs;
    return defs.map((d) => (d.test
      ? { ...d, opzioni: d.opzioni.map((o) => (o.count != null ? o : { ...o, count: items.filter((it) => d.test!(it, o.value)).length })) }
      : d));
  }, [defs, items]);

  const attivi = Object.values(valori).reduce((n, v) => n + v.length, 0);

  return useMemo(() => ({
    defs: defsContate,
    valori,
    valore: (p: string) => valori[p]?.[0] ?? '',
    attivi,
    chiave,
    ha: (p: string, v: string) => (valori[p] ?? []).includes(v),
    toggle,
    set,
    clear,
    applica,
  }), [defsContate, valori, attivi, chiave, toggle, set, clear, applica]);
}

// ════════════════════════════════════════════════════════════════════
// Il controllo
// ════════════════════════════════════════════════════════════════════

/**
 * Sul computer: "Ordina: <scelta> ▾" e "Filtra per ▾" (con il numero dei
 * filtri accesi), più "Togli i filtri" quando ce n'è almeno uno. Va dentro la
 * `.filterbar` della lista, di solito in coda.
 *
 * Sul telefono:
 *  - dentro MobileFilters (o qualunque MobileSheet) si mostra disteso, a
 *    gruppi, con le righe alte quanto un dito;
 *  - fuori da un pannello è un solo bottone ("Ordina e filtra") che apre il
 *    suo pannello dal basso.
 */
export function OrdinaFiltra<T>({
  ordina,
  filtri,
  label,
  allinea = 'destra',
}: {
  ordina?: StatoOrdina<T>;
  filtri?: StatoFiltri<T>;
  /** Il bottone del telefono, fuori dai pannelli. Predefinito: "Ordina e filtra", "Ordina", "Filtra". */
  label?: string;
  /** Da che lato si apre il menu del computer: 'destra' se il controllo è in fondo alla barra. */
  allinea?: 'sinistra' | 'destra';
}) {
  const inFoglio = useContext(DentroFoglio);
  const mobile = useIsMobile();
  const [open, setOpen] = useState(false);

  if (!ordina && !filtri) return null;
  if (inFoglio) return <Disteso ordina={ordina} filtri={filtri} />;

  if (mobile) {
    const testo = label ?? (ordina && filtri ? 'Ordina e filtra' : ordina ? 'Ordina' : 'Filtra');
    const n = filtri?.attivi ?? 0;
    return (
      <>
        <button type="button" className="m-filter-btn of-m-btn" onClick={() => setOpen(true)} aria-haspopup="dialog" aria-expanded={open}>
          <Icon name={filtri ? 'filter' : 'sort'} size={14} />
          <span>{testo}</span>
          {n > 0 && <span className="n">{n}</span>}
        </button>
        <MobileSheet
          open={open}
          onClose={() => setOpen(false)}
          title={testo}
          footer={<button className="btn btn-primary m-block" onClick={() => setOpen(false)}>Vedi i risultati</button>}
        >
          <Disteso ordina={ordina} filtri={filtri} />
        </MobileSheet>
      </>
    );
  }

  return (
    <div className="of-bar">
      {ordina && <MenuOrdina ordina={ordina} allinea={allinea} />}
      {filtri && <MenuFiltra filtri={filtri} allinea={allinea} />}
      {filtri && filtri.attivi > 0 && (
        <button type="button" className="btn btn-sm btn-ghost of-clear" onClick={filtri.clear} title="Toglie i filtri scelti: la ricerca resta">
          <Icon name="x" size={10} /> Togli i filtri
        </button>
      )}
    </div>
  );
}

/**
 * Un gruppo senza voci non si mostra: succede quando le opzioni arrivano
 * dall'API (chi segue, le fasce) e non ce n'è ancora nessuna. Il filtro resta
 * definito, quindi un valore già nell'URL continua a valere.
 */
const conOpzioni = (d: { opzioni: ReadonlyArray<unknown> }) => d.opzioni.length > 0;

/** Chiude con Esc mentre il menu è aperto. */
function useEsc(open: boolean, close: () => void) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);
}

function Pop({ open, onClose, allinea, label, children }: {
  open: boolean; onClose: () => void; allinea: 'sinistra' | 'destra'; label: string; children: ReactNode;
}) {
  useEsc(open, onClose);
  if (!open) return null;
  return (
    <>
      <div className="fixed inset-0 z-[60]" onClick={onClose} aria-hidden="true" />
      <div className={`dropdown of-pop z-[61]${allinea === 'sinistra' ? ' of-pop--sx' : ''}`} role="menu" aria-label={label}>
        {children}
      </div>
    </>
  );
}

function MenuOrdina<T>({ ordina, allinea }: { ordina: StatoOrdina<T>; allinea: 'sinistra' | 'destra' }) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  return (
    <div className="of-menu">
      <button type="button" className="btn btn-sm of-trigger" aria-haspopup="menu" aria-expanded={open}
        title={`Ordina per: ${ordina.attiva.label}`} onClick={() => setOpen((v) => !v)}>
        <Icon name="sort" size={12} />
        <span className="of-pre">Ordina:</span>
        <span className="of-val">{ordina.attiva.label}</span>
        <Icon name="chevronD" size={10} />
      </button>
      <Pop open={open} onClose={close} allinea={allinea} label="Ordina per">
        <div className="of-hd t-micro">Ordina per</div>
        {ordina.opzioni.map((o) => {
          const on = o.key === ordina.key;
          return (
            <button key={o.key} type="button" role="menuitemradio" aria-checked={on} className="of-row actions-menu-row"
              onClick={() => { ordina.set(o.key); setOpen(false); }}>
              <span className="of-tick">{on && <Icon name="check" size={12} />}</span>
              <span className="of-row-lbl">{o.label}</span>
            </button>
          );
        })}
      </Pop>
    </div>
  );
}

function MenuFiltra<T>({ filtri, allinea }: { filtri: StatoFiltri<T>; allinea: 'sinistra' | 'destra' }) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const n = filtri.attivi;
  return (
    <div className="of-menu">
      <button type="button" className={`btn btn-sm of-trigger${n > 0 ? ' interactable-active' : ''}`} aria-haspopup="menu" aria-expanded={open}
        title={n > 0 ? `${n === 1 ? '1 filtro acceso' : `${n} filtri accesi`}` : 'Mostra solo una parte della lista'}
        onClick={() => setOpen((v) => !v)}>
        <Icon name="filter" size={12} />
        <span>Filtra per</span>
        {n > 0 && <span className="of-n">{n}</span>}
        <Icon name="chevronD" size={10} />
      </button>
      <Pop open={open} onClose={close} allinea={allinea} label="Filtra per">
        {filtri.defs.filter(conOpzioni).map((d) => (
          <div key={d.param} className="of-group" role="group" aria-label={d.label}>
            <div className="of-hd t-micro">{d.label}</div>
            {d.opzioni.map((o) => {
              const on = filtri.ha(d.param, o.value);
              const vuota = !on && o.count === 0;
              return (
                <button key={o.value} type="button" role={d.multi ? 'menuitemcheckbox' : 'menuitemradio'} aria-checked={on}
                  className="of-row actions-menu-row" disabled={vuota} onClick={() => filtri.toggle(d.param, o.value)}>
                  {d.multi
                    ? <span className="of-box" aria-hidden="true">{on && <Icon name="check" size={10} strokeWidth={2.5} />}</span>
                    : <span className="of-tick">{on && <Icon name="check" size={12} />}</span>}
                  <span className="of-row-lbl">{o.label}</span>
                  {o.count != null && <span className="of-c">{o.count}</span>}
                </button>
              );
            })}
          </div>
        ))}
        <div className="of-ft">
          <button type="button" className="btn btn-sm btn-ghost" disabled={n === 0} onClick={filtri.clear}>
            <Icon name="x" size={10} /> Togli i filtri
          </button>
          <button type="button" className="btn btn-sm" onClick={close}>Fatto</button>
        </div>
      </Pop>
    </div>
  );
}

/** Dentro un pannello dal basso: gruppi uno sotto l'altro, righe alte quanto un dito. */
function Disteso<T>({ ordina, filtri }: { ordina?: StatoOrdina<T>; filtri?: StatoFiltri<T> }) {
  return (
    <div className="of-disteso">
      {ordina && (
        <div className="m-sheet-group" role="radiogroup" aria-label="Ordina per">
          <div className="m-sheet-group-label">Ordina per</div>
          {ordina.opzioni.map((o) => {
            const on = o.key === ordina.key;
            return (
              <button key={o.key} type="button" role="radio" aria-checked={on} className="m-sheet-row of-srow" onClick={() => ordina.set(o.key)}>
                <span className="m-sheet-row-lbl">{o.label}</span>
                {on && <Icon name="check" size={16} className="of-srow-on" />}
              </button>
            );
          })}
        </div>
      )}
      {filtri?.defs.filter(conOpzioni).map((d) => (
        <div key={d.param} className="m-sheet-group" role="group" aria-label={d.label}>
          <div className="m-sheet-group-label">{d.label}</div>
          {d.opzioni.map((o) => {
            const on = filtri.ha(d.param, o.value);
            const vuota = !on && o.count === 0;
            return (
              <button key={o.value} type="button" role={d.multi ? 'checkbox' : 'radio'} aria-checked={on}
                className="m-sheet-row of-srow" disabled={vuota} onClick={() => filtri.toggle(d.param, o.value)}>
                <span className="m-sheet-row-lbl">{o.label}</span>
                {o.count != null && <span className="m-sheet-row-tag">{o.count}</span>}
                <span className="of-srow-mark">{on && <Icon name="check" size={16} className="of-srow-on" />}</span>
              </button>
            );
          })}
        </div>
      ))}
      {filtri && filtri.attivi > 0 && (
        <button type="button" className="btn of-srow-clear" onClick={filtri.clear}>
          <Icon name="x" size={12} /> Togli i filtri
        </button>
      )}
    </div>
  );
}
